import { FinancialObligationOptions, GlobalControl, OptionQuestion } from '@hb/domain';
import { QuestionKey } from '@hb/modules/global/global.constants';
import { AnalyticsService } from '@hb/services/analytics/analytics.service';
import { FinancialObligationsStoreService } from '@hb/modules/financial-obligations/financial-obligations-store.service';

import { FormGroup } from '@angular/forms';
import { inject } from '@angular/core';

export abstract class AbstractOption {
    protected readonly store = inject(FinancialObligationsStoreService);
    protected readonly analyticsService = inject(AnalyticsService);

    readonly options = Object.values(OptionQuestion);
    optionsState: FinancialObligationOptions = this.store.getFinancialObligationOptionsState();
    optionsForm: FormGroup = this.buildOptionsForm();

    constructor() {
        this.optionsForm.valueChanges.subscribe((options) => {
            this.store.setFinancialObligationsOptions(options as FinancialObligationOptions);
            this.optionsState = options as FinancialObligationOptions;
        });
    }

    isInvalid(): boolean {
        return !Object.keys(this.optionsForm.value)
            .map((option) => (this.optionsForm.value as FinancialObligationOptions)[option])
            .filter((option) => option !== null)
            .includes(true);
    }

    showError(): boolean {
        return this.isInvalid() && this.optionsForm.touched;
    }

    select(): void {
        this.optionsForm.controls.noObligations.reset(null);
    }

    noObligations(): void {
        this.optionsForm.controls.studentDebt.reset(null);
        this.optionsForm.controls.paymentAccountOverdraft.reset(null);
        this.optionsForm.controls.mobilePhonePayment.reset(null);
        this.optionsForm.controls.openEndedCredit.reset(null);
        this.optionsForm.controls.creditCard.reset(null);
        this.optionsForm.controls.privateLease.reset(null);
        this.optionsForm.controls.personalLoan.reset(null);
        this.optionsForm.controls.partnerAlimony.reset(null);
    }

    private buildOptionsForm(): FormGroup {
        const state = this.optionsState;

        return new FormGroup({
            studentDebt: new GlobalControl(this.analyticsService, QuestionKey.OPTION_STUDENT_DEBT, state ? state.studentDebt : null),
            paymentAccountOverdraft: new GlobalControl(
                this.analyticsService,
                QuestionKey.OPTION_PAYMENT_ACCOUNT_OVERDRAFT,
                state ? state.paymentAccountOverdraft : null
            ),
            mobilePhonePayment: new GlobalControl(
                this.analyticsService,
                QuestionKey.OPTION_MOBILE_PHONE,
                state ? state.mobilePhonePayment : null
            ),
            openEndedCredit: new GlobalControl(
                this.analyticsService,
                QuestionKey.OPTION_OPEN_ENDED_CREDIT,
                state ? state.openEndedCredit : null
            ),
            creditCard: new GlobalControl(this.analyticsService, QuestionKey.OPTION_CREDIT_CARD, state ? state.creditCard : null),
            privateLease: new GlobalControl(this.analyticsService, QuestionKey.OPTION_PRIVATE_LEASE, state ? state.privateLease : null),
            personalLoan: new GlobalControl(this.analyticsService, QuestionKey.OPTION_PERSONAL_LOAN, state ? state.personalLoan : null),
            partnerAlimony: new GlobalControl(
                this.analyticsService,
                QuestionKey.OPTION_PARTNER_ALIMONY,
                state ? state.partnerAlimony : null
            ),
            noObligations: new GlobalControl(this.analyticsService, QuestionKey.OPTION_NONE, state ? state.noObligations : null)
        });
    }
}
